import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { setPageMeta } from '../utils/seo';
import './SapienShield.css';
import sapienbg1 from '../Assets/sapienbg1.png';
import sapienbg2 from '../Assets/sapienbg2.png';
import sapienbg3 from '../Assets/sapienbg3.png';
import sapienbg4 from '../Assets/sapienbg4.png';
import sapienbg5 from '../Assets/sapienbg5.png';

const fadeUp = {
  hidden: { opacity: 0, y: 45 },
  visible: { opacity: 1, y: 0 }
};

const SapienShield = () => {
  useEffect(() => {
    setPageMeta(
      'SapienShield AI Firewall | Sapiencia.AI',
      'SapienShield protects enterprise LLM applications with input scanning, output validation, risk scoring, and AI-native security controls.'
    );
  }, []);

  return (
    <div className="sapienshield-page">
      <Navbar />

      <main className="sapienshield-main">
        {/* HERO */}
        <section
          className="shield-section shield-hero"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.55)), url(${sapienbg1})`
          }}
        >
          <motion.div
            className="shield-hero-content"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.8 }}
          >
            <p className="shield-eyebrow">SapienShield AI Firewall</p>
            <h1>Securing the AI Layer From Prompt to Response</h1>
            <p className="shield-hero-text">
              SapienShield protects enterprise LLM applications by inspecting user prompts before they reach the model
              and validating model responses before they return to the user.
            </p>

            <div className="shield-hero-buttons">
              <Link to="/contact" className="shield-btn primary">
                Request Demo
              </Link>
              <a href="#how-it-works" className="shield-btn secondary">
                See How It Works
              </a>
            </div>
          </motion.div>
        </section>

        {/* HOW IT WORKS */}
        <section
          className="shield-section"
          id="how-it-works"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.40), rgba(0,0,0,0.55)), url(${sapienbg2})`
          }}
        >
          <motion.div
            className="shield-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.7 }}
          >
            <p className="shield-eyebrow">How It Works</p>
            <h2>Protection Built Into the AI Request Flow</h2>
            <p>
              SapienShield sits between the application and the language model, scanning both sides of the interaction.
            </p>
          </motion.div>

          <motion.div
            className="shield-flow"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.25 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <div className="flow-card">User / Application</div>
            <span>→</span>
            <div className="flow-card highlight">Input Scan + Risk Score</div>
            <span>→</span>
            <div className="flow-card">LLM</div>
            <span>→</span>
            <div className="flow-card highlight">Output Scan + Redaction</div>
            <span>→</span>
            <div className="flow-card">Protected Response</div>
          </motion.div>
        </section>

        {/* CORE PROTECTION */}
        <section
          className="shield-section"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.60)), url(${sapienbg3})`
          }}
        >
          <motion.div
            className="shield-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.7 }}
          >
            <p className="shield-eyebrow">Core Protection</p>
            <h2>AI-Native Security Controls</h2>
          </motion.div>

          <div className="shield-card-grid">
            {[
              {
                title: 'Input Scanning',
                text: 'Detects prompt injections, jailbreaks, malicious code, obfuscation, and abuse patterns before the model sees them.'
              },
              {
                title: 'Risk Scoring',
                text: 'Assigns a real-time risk score based on safety, intent, sensitivity, compliance, and adversarial likelihood.'
              },
              {
                title: 'Output Protection',
                text: 'Inspects model responses for PII, toxic content, confidential data, hallucination risk, and policy violations.'
              }
            ].map((item, index) => (
              <motion.div
                className="shield-card"
                key={item.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.3 }}
                transition={{ duration: 0.65, delay: index * 0.12 }}
              >
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* TECHNOLOGY STACK */}
        <section
          className="shield-section"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.60)), url(${sapienbg4})`
          }}
        >
          <motion.div
            className="shield-section-header"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.7 }}
          >
            <p className="shield-eyebrow">Technology Stack</p>
            <h2>Designed to Fit Modern Enterprise AI Systems</h2>
            <p>
              SapienShield is model-agnostic and can support major LLM providers, REST API integrations, reverse proxy
              deployment, and on-premise environments.
            </p>
          </motion.div>

          <motion.div
            className="stack-grid"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.25 }}
            transition={{ duration: 0.7 }}
          >
            <div>OpenAI</div>
            <div>Claude</div>
            <div>Gemini</div>
            <div>REST API</div>
            <div>Reverse Proxy</div>
            <div>On-Prem Support</div>
          </motion.div>
        </section>



        {/* CTA */}
        <section
          className="shield-section shield-cta"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.60)), url(${sapienbg5})`
          }}
        >
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}
            transition={{ duration: 0.7 }}
          >
            <h2>Ready to Secure Your AI Applications?</h2>
            <p>
              Add an intelligent security layer to your LLM systems without rebuilding your entire AI stack.
            </p>
            <Link to="/contact" className="shield-btn primary">
              Contact Sapiencia.AI
            </Link>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SapienShield;